// 页：流量分析（IDP spec §4.9 前端）
// 5 tab：总览 / 会话与路径 / 漏斗与留存 / 热区 / 实时事件流
// spec 锚点：
//   - 三层身份：device_id(_shopify_y 1y) / session_id(_shopify_s 30min) / customer_id
//   - sessionization 客户端切（30min 超时），engagement_time 客户端算
//   - 接口契约 POST /idp/events ≤50 events/请求；GET /idp/experiments/config 强缓存
//   - 写路径无回查无 UPDATE，维表 ID 解析 cache + ON DUPLICATE
//   - 数字化验收 4 项见数据健康页

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Card, Row, Col, Statistic, Tag, Select, Empty, Progress, Space, Table, Timeline, Alert, Input, Button, Tooltip } from 'antd';
import { Tabs } from '../AntdCompat';
import { ClockCircleOutlined, DesktopOutlined, MobileOutlined, TabletOutlined } from '@ant-design/icons';
import RangeSelect from '@/components/RangeSelect';
import { SITE_NAMES } from '../data';
import { EventType, DeviceType, FunnelStep, IdpEvent, IdpSessionAgg } from '../types';
import {
  getIdpAggregate,
  getIdpCohort,
  getIdpDestFunctions,
  getIdpFunnel,
  getIdpHeatmap,
  getIdpOverview,
  getIdpPathFlow,
  getIdpRealtime,
  getIdpSessions,
} from '@/services/idpAnalytics';
import {
  WEB_KEY_TO_SITE,
  webKeysForFilter,
} from '../siteWebKeys';
import { useIdpSiteAccess } from '../useIdpSiteAccess';
import type {
  IdpAggregateDTO,
  IdpAggregateRow,
  IdpCohortDTO,
  IdpFunnelDTO,
  IdpHeatmapDTO,
  IdpOverviewDTO,
  IdpPathFlowDTO,
  IdpRealtimeEventDTO,
  IdpSessionDetailDTO,
  IdpSessionListDTO,
  IdpSessionSummaryDTO,
  IdpWebKey,
} from '@/services/idpAnalytics';

// 节点 pageType → 色卡（链路 + 节点共享）
const sankeyColorByType = (pt?: string) => {
  switch (pt) {
    case 'HOMEPAGE': return '#1890ff';
    case 'COLLECTION': return '#13c2c2';
    case 'PDP': return '#722ed1';
    case 'CART': return '#fa8c16';
    case 'CHECKOUT': return '#eb2f96';
    case 'BLOG': return '#52c41a';
    case 'ACTION': return '#fa541c';
    case 'EXIT': return '#8c8c8c';
    default: return '#bfbfbf';
  }
};

// 桑基图组件（纯手撸 SVG 不依赖 d3-sankey）
// 关键不变量（d3-sankey 标准）：
//   1. 全局统一 yScale = (availableH - 最多节点列的 gap) / max(每列 totalUsers)
//      → 所有列节点高度同 scale；不再每列各自 scale 致 ribbon 不对齐
//   2. ribbon 用 area path (fill)，上下两条贝塞尔曲线包住一个带状区域
//      → 不再用 stroke + strokeWidth；ribbon 宽 = link.value × yScale 严格守恒
//   3. 节点高度 = sum(incoming valueH) = sum(outgoing valueH)，肉眼可对账
const SankeyChart: React.FC<{
  data: { nodes: any[]; links: any[]; totalStart: number };
  width?: number;
  height?: number;
}> = ({ data, width = 900, height = 460 }) => {
  if (data.nodes.length === 0) {
    return <Empty description="无路径数据" />;
  }
  const PADDING_X = 20;
  const PADDING_Y = 10;
  const NODE_W = 14;
  const NODE_GAP = 4;

  const maxDepth = Math.max(...data.nodes.map(n => n.depth));
  const columnGap = maxDepth > 0 ? (width - PADDING_X * 2 - NODE_W) / maxDepth : 0;

  // 1. 分列 + 同列排序（EXIT 垫底，其他按 users desc）
  const columns: Array<Array<typeof data.nodes[0]>> = [];
  for (let d = 0; d <= maxDepth; d++) {
    const colNodes = data.nodes
      .filter(n => n.depth === d)
      .sort((a, b) => {
        if (a.pageType === 'EXIT' && b.pageType !== 'EXIT') return 1;
        if (b.pageType === 'EXIT' && a.pageType !== 'EXIT') return -1;
        return b.users - a.users;
      });
    columns.push(colNodes);
  }

  // 2. 全局统一 yScale
  //    取「总 users 最多的那一列」作为高度基准 — 其他列按同 scale 自然变矮居中显示
  const maxColumnTotalUsers = Math.max(...columns.map(c => c.reduce((s, n) => s + n.users, 0)), 1);
  const maxNodesInCol = Math.max(...columns.map(c => c.length), 1);
  const maxColumnGapPx = (maxNodesInCol - 1) * NODE_GAP;
  const availableH = height - PADDING_Y * 2;
  const yScale = maxColumnTotalUsers > 0
    ? (availableH - maxColumnGapPx) / maxColumnTotalUsers
    : 0;

  // 3. 节点 layout（同一 yScale，列内居中）
  const layout: Record<string, { x0: number; x1: number; y0: number; y1: number; node: typeof data.nodes[0] }> = {};
  for (let d = 0; d <= maxDepth; d++) {
    const colNodes = columns[d];
    const totalUsers = colNodes.reduce((s, n) => s + n.users, 0);
    const colHeight = totalUsers * yScale + (colNodes.length - 1) * NODE_GAP;
    let yCursor = PADDING_Y + (availableH - colHeight) / 2;
    const x0 = PADDING_X + d * columnGap;
    for (const n of colNodes) {
      const h = n.users * yScale;
      layout[n.id] = { x0, x1: x0 + NODE_W, y0: yCursor, y1: yCursor + h, node: n };
      yCursor += h + NODE_GAP;
    }
  }

  // 4. link area path —— 上下两条贝塞尔包围；宽度严格 = link.value × yScale
  //    sort by source.y0 然后 target.y0：让 ribbon 在 source/target 端按位置堆叠，减少交叉
  const linkPaths: Array<{
    d: string; color: string;
    sourceLabel: string; targetLabel: string; value: number;
  }> = [];
  const outCursor: Record<string, number> = {};
  const inCursor: Record<string, number> = {};
  const sortedLinks = data.links.slice().sort((a, b) => {
    const aSrcY = layout[a.source]?.y0 ?? 0;
    const bSrcY = layout[b.source]?.y0 ?? 0;
    if (aSrcY !== bSrcY) return aSrcY - bSrcY;
    const aTgtY = layout[a.target]?.y0 ?? 0;
    const bTgtY = layout[b.target]?.y0 ?? 0;
    return aTgtY - bTgtY;
  });

  for (const l of sortedLinks) {
    const src = layout[l.source];
    const tgt = layout[l.target];
    if (!src || !tgt) continue;
    const valueH = l.value * yScale;
    if (valueH < 0.5) continue; // 太细的 ribbon 不画（视觉噪声）

    const sy0 = src.y0 + (outCursor[l.source] || 0);
    const sy1 = sy0 + valueH;
    const ty0 = tgt.y0 + (inCursor[l.target] || 0);
    const ty1 = ty0 + valueH;
    outCursor[l.source] = (outCursor[l.source] || 0) + valueH;
    inCursor[l.target] = (inCursor[l.target] || 0) + valueH;

    const sx = src.x1;
    const tx = tgt.x0;
    const midX = (sx + tx) / 2;
    // 上沿贝塞尔 → tgt 端直线下到下沿 → 下沿反向贝塞尔回 src → 直线 close
    const path =
      `M${sx},${sy0} ` +
      `C${midX},${sy0} ${midX},${ty0} ${tx},${ty0} ` +
      `L${tx},${ty1} ` +
      `C${midX},${ty1} ${midX},${sy1} ${sx},${sy1} ` +
      `Z`;

    linkPaths.push({
      d: path,
      color: sankeyColorByType(tgt.node.pageType),
      sourceLabel: src.node.label,
      targetLabel: tgt.node.label,
      value: l.value,
    });
  }

  return (
    <svg width={width} height={height} style={{ background: '#fafafa', borderRadius: 4 }}>
      {linkPaths.map((l, i) => (
        <Tooltip
          key={i}
          title={<span>{l.sourceLabel} → {l.targetLabel}<br /><strong>{l.value}</strong> users</span>}
        >
          <path
            d={l.d}
            fill={l.color}
            fillOpacity={0.4}
            stroke="none"
            style={{ cursor: 'pointer', transition: 'fill-opacity 0.2s' }}
            onMouseEnter={e => (e.currentTarget.style.fillOpacity = '0.65')}
            onMouseLeave={e => (e.currentTarget.style.fillOpacity = '0.4')}
          />
        </Tooltip>
      ))}
      {Object.values(layout).map(({ x0, x1, y0, y1, node }) => (
        <g key={node.id}>
          <Tooltip title={<span>{node.label}<br />depth {node.depth} · {node.users} users</span>}>
            <rect
              x={x0}
              y={y0}
              width={x1 - x0}
              height={Math.max(2, y1 - y0)}
              fill={sankeyColorByType(node.pageType)}
              style={{ cursor: 'pointer' }}
            />
          </Tooltip>
          {(y1 - y0) > 10 && (
            <text
              x={node.depth < maxDepth / 2 ? x1 + 4 : x0 - 4}
              y={(y0 + y1) / 2}
              dy={3}
              fontSize={10}
              fill="#262626"
              textAnchor={node.depth < maxDepth / 2 ? 'start' : 'end'}
              style={{ pointerEvents: 'none' }}
            >
              {node.label}<tspan fill="#8c8c8c"> ({node.users})</tspan>
            </text>
          )}
        </g>
      ))}
    </svg>
  );
};

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  page_view: '页面浏览', click: '点击', scroll: '滚动',
  add_to_cart: '加购', checkout_start: '结账开始', purchase: '购买', inquiry_open: '询单',
};
const EVENT_TYPE_COLORS: Record<EventType, string> = {
  page_view: 'blue', click: 'cyan', scroll: 'geekblue',
  add_to_cart: 'orange', checkout_start: 'gold', purchase: 'green', inquiry_open: 'magenta',
};

const DEVICE_ICONS: Record<DeviceType, React.ReactNode> = {
  mobile: <MobileOutlined />, desktop: <DesktopOutlined />, tablet: <TabletOutlined />,
};

// 默认 4 步漏斗
const DEFAULT_FUNNEL: FunnelStep[] = [
  { step_no: 1, name: '到访首页', match: { page_type: 'HOMEPAGE' }, user_count: 0, drop_off_pct: 0 },
  { step_no: 2, name: '看商品 PDP', match: { page_type: 'PDP' }, user_count: 0, drop_off_pct: 0 },
  { step_no: 3, name: '加入购物车', match: { event_type: 'add_to_cart' }, user_count: 0, drop_off_pct: 0 },
  { step_no: 4, name: '完成结账', match: { event_type: 'purchase' }, user_count: 0, drop_off_pct: 0 },
];

type ApiSessionSummary = IdpSessionSummaryDTO & { webKey: IdpWebKey };
type ApiSessionDetail = IdpSessionDetailDTO & { webKey: IdpWebKey };

type TrafficApiState = {
  loading: boolean;
  source: 'api' | 'mock';
  error?: string;
  overview?: IdpOverviewDTO;
  sessions: ApiSessionSummary[];
  detail?: ApiSessionDetail;
  funnel?: IdpFunnelDTO;
  heatmap?: IdpHeatmapDTO;
  realtime: IdpRealtimeEventDTO[];
  pathflow?: IdpPathFlowDTO;
  aggregate?: IdpAggregateDTO;
  destFunctions?: string[];
  cohort?: IdpCohortDTO;
};

const emptyApiState: TrafficApiState = {
  loading: false,
  source: 'mock',
  sessions: [],
  realtime: [],
};

type Settled<T> = { ok: true; value: T } | { ok: false; reason: any };

function settle<T>(promise: Promise<T>): Promise<Settled<T>> {
  return promise
    .then(value => ({ ok: true, value } as Settled<T>))
    .catch(reason => ({ ok: false, reason } as Settled<T>));
}

function errorMessage(reason: any) {
  return reason?.message || reason?.msg || String(reason || 'request failed');
}

function emptyOverview(webKey: IdpWebKey): IdpOverviewDTO {
  return {
    webKey,
    pv: 0,
    uv: 0,
    clicks: 0,
    dau: 0,
    wau: 0,
    mau: 0,
    trend: [],
    identityDistribution: [],
    deviceDistribution: [],
    eventTypeDistribution: [],
  };
}

function emptySessions(webKey: IdpWebKey): IdpSessionListDTO {
  return { webKey, sessions: [] };
}

function emptyFunnel(webKey: IdpWebKey): IdpFunnelDTO {
  return { webKey, steps: [] };
}

function toApiDateTime(date: Date) {
  return date.toISOString().slice(0, 19);
}

function normalizeDeviceType(value?: string): DeviceType {
  const v = String(value || '').toLowerCase();
  if (v.indexOf('desktop') > -1 || v === 'pc' || v === '0') return 'desktop';
  if (v.indexOf('tablet') > -1 || v.indexOf('pad') > -1) return 'tablet';
  return 'mobile';
}

function normalizeEventType(value?: string): EventType {
  const v = String(value || '').toLowerCase();
  const byServerName: Record<string, EventType> = {
    page_view: 'page_view',
    pageview: 'page_view',
    click: 'click',
    scroll: 'scroll',
    add_to_cart: 'add_to_cart',
    checkout_start: 'checkout_start',
    purchase: 'purchase',
    inquiry_open: 'inquiry_open',
    page_view_event: 'page_view',
    pageview_event: 'page_view',
  };
  return byServerName[v] || byServerName[v.replace(/-/g, '_')] || 'page_view';
}

function eventLabel(stepKey?: string) {
  const type = normalizeEventType(stepKey);
  return EVENT_TYPE_LABELS[type] || stepKey || '-';
}

function epochToIso(value?: number) {
  return value ? new Date(value).toISOString() : new Date().toISOString();
}

function sumDimension(points: Array<{ name?: string; value?: number }> | undefined, normalizer?: (name?: string) => string) {
  return (points || []).reduce((acc, item) => {
    const key = normalizer ? normalizer(item.name) : (item.name || 'unknown');
    acc[key] = (acc[key] || 0) + (item.value || 0);
    return acc;
  }, {} as Record<string, number>);
}

function aggregateOverview(list: IdpOverviewDTO[]): IdpOverviewDTO | undefined {
  if (!list.length) return undefined;
  const trendMap: Record<string, { day: string; pv: number; uv: number; clicks: number }> = {};
  const identity = {};
  const device = {};
  const eventTypes = {};

  list.forEach(item => {
    (item.trend || []).forEach(p => {
      if (!trendMap[p.day]) trendMap[p.day] = { day: p.day, pv: 0, uv: 0, clicks: 0 };
      trendMap[p.day].pv += p.pv || 0;
      trendMap[p.day].uv += p.uv || 0;
      trendMap[p.day].clicks += p.clicks || 0;
    });
    Object.assign(identity, sumObjects(identity, sumDimension(item.identityDistribution)));
    Object.assign(device, sumObjects(device, sumDimension(item.deviceDistribution, v => normalizeDeviceType(v))));
    Object.assign(eventTypes, sumObjects(eventTypes, sumDimension(item.eventTypeDistribution, v => normalizeEventType(v))));
  });

  return {
    webKey: list[0].webKey,
    pv: list.reduce((s, item) => s + (item.pv || 0), 0),
    uv: list.reduce((s, item) => s + (item.uv || 0), 0),
    clicks: list.reduce((s, item) => s + (item.clicks || 0), 0),
    dau: list.reduce((s, item) => s + (item.dau || 0), 0),
    wau: list.reduce((s, item) => s + (item.wau || 0), 0),
    mau: list.reduce((s, item) => s + (item.mau || 0), 0),
    trend: Object.values(trendMap).sort((a, b) => a.day.localeCompare(b.day)).slice(-14),
    identityDistribution: objectToDimension(identity),
    deviceDistribution: objectToDimension(device),
    eventTypeDistribution: objectToDimension(eventTypes),
  };
}

function sumObjects(a: Record<string, number>, b: Record<string, number>) {
  const next = { ...a };
  Object.keys(b).forEach(key => {
    next[key] = (next[key] || 0) + b[key];
  });
  return next;
}

function objectToDimension(obj: Record<string, number>) {
  return Object.keys(obj).map(name => ({ name, value: obj[name] }));
}

function mapApiEvent(event: IdpRealtimeEventDTO): IdpEvent {
  return {
    id: event.eventId || `${event.webKey}_${event.eventTime}_${event.sessionId}`,
    event_time: epochToIso(event.eventTime),
    event_type: normalizeEventType(event.eventType),
    device_id: event.deviceId || '',
    session_id: event.sessionId || '',
    customer_id: event.customerId,
    page_id: event.pageId,
    referrer: event.referrer,
    element_id: event.elementId,
    device_type: normalizeDeviceType(event.deviceType),
    scroll_depth: event.scrollDepth,
    engagement_ms: event.engagementMs,
    site_id: WEB_KEY_TO_SITE[event.webKey] ?? 2,
    props: {
      ...(event.props || {}),
      pageUrl: event.pageUrl,
      elementText: event.elementText,
    },
  };
}

function mapApiSession(summary: ApiSessionSummary, detail?: ApiSessionDetail): IdpSessionAgg {
  const events = detail?.sessionId === summary.sessionId ? (detail.events || []).map(mapApiEvent) : [];
  const pageEvents = events.filter(e => e.page_id);
  const firstEvent = events[0];
  const lastEvent = events[events.length - 1];
  const firstPage = pageEvents[0];
  const lastPage = pageEvents[pageEvents.length - 1];
  const startedAt = summary.startedAt || detail?.startedAt || firstEvent?.event_time && new Date(firstEvent.event_time).getTime();
  const endedAt = summary.endedAt || detail?.endedAt || lastEvent?.event_time && new Date(lastEvent.event_time).getTime();

  return {
    session_id: summary.sessionId,
    device_id: summary.deviceId || detail?.deviceId || '',
    customer_id: summary.customerId || detail?.customerId,
    site_id: WEB_KEY_TO_SITE[summary.webKey] ?? 2,
    device_type: normalizeDeviceType(summary.deviceType || detail?.deviceType),
    start_at: epochToIso(startedAt || undefined),
    end_at: epochToIso(endedAt || undefined),
    duration_ms: summary.durationMs || detail?.durationMs || (startedAt && endedAt ? endedAt - startedAt : 0),
    page_view_count: events.length ? events.filter(e => e.event_type === 'page_view').length : 0,
    click_count: events.length ? events.filter(e => e.event_type === 'click').length : 0,
    page_path: pageEvents.map(e => ({
      page_id: e.page_id || 0,
      url: String(e.props?.pageUrl || summary.entryUrl || ''),
      title: String(e.props?.pageUrl || summary.entryUrl || `page#${e.page_id}`),
      arrived_at: e.event_time,
      engagement_ms: e.engagement_ms || 0,
    })),
    has_atc: events.some(e => e.event_type === 'add_to_cart'),
    has_purchase: events.some(e => e.event_type === 'purchase'),
    has_inquiry: events.some(e => e.event_type === 'inquiry_open'),
    exit_page_id: lastPage?.page_id || 0,
    exit_page_url: summary.exitUrl || detail?.exitUrl || String(lastPage?.props?.pageUrl || ''),
    entry_page_id: firstPage?.page_id || 0,
    entry_page_url: summary.entryUrl || detail?.entryUrl || String(firstPage?.props?.pageUrl || ''),
    event_count: summary.eventCount || detail?.eventCount || events.length || 0,
  };
}

// destFunction label 的模块前缀（"区域：目标" 格式，对齐 v1 模块概念；BF 实测 52/52 可切）
function moduleOf(label?: string) {
  if (!label) return '其他';
  const idx = label.indexOf('：');
  return idx > 0 ? label.slice(0, idx) : '其他';
}

// pathflow 节点显示名：title > url path > page#id
function pageNodeLabel(node?: { pageId: number; url?: string; title?: string }) {
  if (!node) return '';
  if (node.title) return node.title;
  if (node.url) {
    try {
      const u = new URL(node.url);
      return `${u.pathname}${u.search}` || node.url;
    } catch (e) {
      return node.url;
    }
  }
  return `page#${node.pageId}`;
}


type HeatmapOverlayPoint = {
  x: number;
  y: number;
  intensity: number;
  selector?: string;
  elementText?: string;
  clicks?: number;
  uniqueUsers?: number;
};


// 截图产物公开桶（与 erpify chunks 同桶直链）；SG 管线产出，key 约定见 ops/idp-heatmap-capture/README.md
const IDP_SHOT_BASE = 'https://www.hellozm.com';
const IDP_WEB_ID: Record<string, number> = { FG: 1, ST: 2, BF: 3 };

type ShotLayout = {
  pageHeight: number;
  viewport: { w: number; h: number };
  elements: Record<string, { x: number; y: number; w: number; h: number }>;
  missing?: string[];
  capturedAt?: string;
  url?: string;
};

// 热区主视图（方案 A）：整页截图打底 + 三图层（元素染色 / 斑点 / scroll 色带）。
// 截图或 layout 拉不到 → onUnavailable 回落元素排行视图。
const ScreenshotHeatmap: React.FC<{
  webId: number;
  pageId: number;
  device: 'desktop' | 'mobile';
  points: HeatmapOverlayPoint[];
  scrollBuckets?: Array<{ bucket: number; users: number }>;
  onUnavailable: () => void;
}> = ({ webId, pageId, device, points, scrollBuckets, onUnavailable }) => {
  const base = `${IDP_SHOT_BASE}/idp-heatmap/${webId}/${pageId}/${device}`;
  const [layout, setLayout] = useState<ShotLayout | null>(null);
  const [scale, setScale] = useState(0);
  const [imgH, setImgH] = useState(0);
  const [layers, setLayers] = useState({ boxes: true, spots: true, scroll: false });

  useEffect(() => {
    let dead = false;
    setLayout(null);
    setScale(0);
    window.fetch(`${base}/latest.layout.json`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(l => { if (!dead) setLayout(l); })
      .catch(() => { if (!dead) onUnavailable(); });
    return () => { dead = true; };
  }, [base]);

  if (!layout) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c', fontSize: 12 }}>截图加载中…</div>;
  }

  const matched = points.filter(pt => pt.selector && layout.elements[pt.selector]);
  const unmatched = points.filter(pt => pt.selector && !layout.elements[pt.selector]);
  const maxClicks = Math.max(...matched.map(pt => pt.clicks || 0), 1);
  const totalScroll = (scrollBuckets || []).reduce((sum, b) => sum + (b.users || 0), 0);
  const reachedAtLeast = (start: number) => (scrollBuckets || [])
    .filter(b => b.bucket >= start)
    .reduce((sum, b) => sum + (b.users || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 11, alignItems: 'center' }}>
        {([
          ['boxes', '元素染色'],
          ['spots', '点击斑点'],
          ['scroll', 'scroll 色带'],
        ] as Array<[keyof typeof layers, string]>).map(([key, label]) => (
          <label key={key} style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={layers[key]}
              onChange={e => setLayers({ ...layers, [key]: e.target.checked })}
              style={{ marginRight: 4, verticalAlign: 'middle' }}
            />
            {label}
          </label>
        ))}
        <span style={{ color: '#8c8c8c', marginLeft: 'auto' }}>
          截图 {layout.capturedAt || ''} · 命中 {matched.length}/{points.length} 元素
        </span>
      </div>
      <div style={{ maxHeight: 680, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 4 }}>
        <div style={{ position: 'relative' }}>
          <img
            src={`${base}/latest.png`}
            alt="page screenshot"
            style={{ width: '100%', display: 'block' }}
            onLoad={e => {
              const el = e.currentTarget;
              setScale(el.offsetWidth / (el.naturalWidth || layout.viewport.w || 1));
              setImgH(el.offsetHeight);
            }}
            onError={onUnavailable}
          />
          {scale > 0 && layers.scroll && totalScroll > 0 ? [0, 25, 50, 75].map(start => {
            const share = reachedAtLeast(start) / totalScroll;
            return (
              <div key={start} style={{
                position: 'absolute', left: 0, right: 0,
                top: imgH * (start / 100), height: imgH * 0.25,
                background: `rgba(24, 144, 255, ${share * 0.3})`, pointerEvents: 'none',
              }}>
                <span style={{ position: 'absolute', right: 8, top: 4, fontSize: 11, color: '#0958d9', background: 'rgba(255,255,255,0.85)', padding: '0 4px', borderRadius: 2 }}>
                  {(share * 100).toFixed(0)}% 看到这里
                </span>
              </div>
            );
          }) : null}
          {scale > 0 && layers.boxes ? matched.map((pt, i) => {
            const r = layout.elements[pt.selector as string];
            const ratio = (pt.clicks || 0) / maxClicks;
            return (
              <Tooltip key={`box_${i}`} title={<span>{pt.elementText || pt.selector}<br />{pt.clicks || 0} clicks / {pt.uniqueUsers || 0} users<br /><code style={{ fontSize: 10 }}>{pt.selector}</code></span>}>
                <div style={{
                  position: 'absolute',
                  left: r.x * scale, top: r.y * scale, width: r.w * scale, height: r.h * scale,
                  background: `rgba(207, 19, 34, ${0.08 + ratio * 0.35})`,
                  border: `1px solid rgba(207, 19, 34, ${0.4 + ratio * 0.5})`,
                  borderRadius: 2,
                }}>
                  <span style={{ position: 'absolute', top: -1, right: -1, fontSize: 10, background: '#cf1322', color: '#fff', padding: '0 4px', borderRadius: 2 }}>{pt.clicks || 0}</span>
                </div>
              </Tooltip>
            );
          }) : null}
          {scale > 0 && layers.spots ? matched.map((pt, i) => {
            const r = layout.elements[pt.selector as string];
            const cx = (r.x + r.w * ((pt.x || 50) / 100)) * scale;
            const cy = (r.y + r.h * ((pt.y || 50) / 100)) * scale;
            const ratio = (pt.clicks || 0) / maxClicks;
            const size = 22 + ratio * 40;
            return (
              <div key={`spot_${i}`} style={{
                position: 'absolute', left: cx, top: cy, width: size, height: size,
                transform: 'translate(-50%, -50%)', borderRadius: '50%', pointerEvents: 'none',
                background: `radial-gradient(circle, rgba(207,19,34,${0.45 + ratio * 0.35}) 0%, rgba(250,140,22,${0.2 + ratio * 0.2}) 55%, rgba(250,140,22,0) 75%)`,
              }} />
            );
          }) : null}
        </div>
      </div>
      {unmatched.length ? (
        <div style={{ marginTop: 8, fontSize: 11 }}>
          <span style={{ color: '#fa8c16' }}>未匹配点击（theme 改版后 selector 失配，点击数不丢）：</span>
          {unmatched.slice(0, 10).map((pt, i) => (
            <Tag key={i} style={{ fontSize: 10, marginTop: 4 }}>{pt.elementText || pt.selector}（{pt.clicks || 0}）</Tag>
          ))}
          {unmatched.length > 10 ? <span style={{ color: '#8c8c8c' }}>等 {unmatched.length} 个</span> : null}
        </div>
      ) : null}
    </div>
  );
};

// 元素级 click map（Clarity 主视图范式）：按元素聚合排行，不依赖页面坐标回放。
// Shopify 强制 frame-ancestors 'none'，iframe 嵌真实页作废；斑点/截图 overlay 走后续截图方案。
const ElementClickMap: React.FC<{
  pageUrl?: string;
  points: HeatmapOverlayPoint[];
}> = ({ pageUrl, points }) => {
  const ranked = points
    .slice()
    .sort((a, b) => (b.clicks || 0) - (a.clicks || 0))
    .slice(0, 30);
  const maxClicks = ranked[0]?.clicks || 1;
  const totalClicks = points.reduce((sum, item) => sum + (item.clicks || 0), 0);
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1, padding: 10, background: '#f5f5f5', borderRadius: 4, fontSize: 11 }}>
          <strong>{pageUrl || '当前页面'}</strong>
          <span style={{ marginLeft: 8, color: '#8c8c8c' }}>
            按元素聚合的点击热度；x/y 为元素内点击位置均值（元素相对坐标）
          </span>
        </div>
        <div style={{ width: 200, padding: 10, background: '#f5f5f5', borderRadius: 4, fontSize: 11, textAlign: 'center' }}>
          <div style={{ color: '#8c8c8c' }}>当前页面</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#cf1322' }}>{totalClicks.toLocaleString()}</div>
          <div style={{ color: '#8c8c8c' }}>click events</div>
        </div>
      </div>
      {ranked.map((point, index) => {
        const ratio = (point.clicks || 0) / maxClicks;
        return (
          <Tooltip
            key={`${point.selector || index}_${index}`}
            title={<span>
              {point.elementText || point.selector || 'unknown'}
              <br />
              {point.clicks || 0} clicks / {point.uniqueUsers || 0} users
              <br />
              <code style={{ fontSize: 10 }}>{point.selector}</code>
              <br />
              元素内均值位置 x {Math.round(point.x)}% · y {Math.round(point.y)}%
            </span>}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 11 }}>
              <span style={{ width: 24, color: '#8c8c8c', textAlign: 'right' }}>{index + 1}</span>
              <span style={{ width: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {point.elementText || point.selector}
              </span>
              <div style={{ flex: 1, height: 14, background: '#f5f5f5', borderRadius: 2 }}>
                <div style={{
                  width: `${Math.max(ratio * 100, 2)}%`,
                  height: '100%',
                  borderRadius: 2,
                  background: `rgba(207, 19, 34, ${0.25 + ratio * 0.65})`,
                }} />
              </div>
              <span style={{ width: 90, textAlign: 'right' }}>
                <strong>{point.clicks || 0}</strong> <span style={{ color: '#8c8c8c' }}>/ {point.uniqueUsers || 0}u</span>
              </span>
            </div>
          </Tooltip>
        );
      })}
      {points.length > ranked.length && (
        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 8 }}>只显示 Top {ranked.length} / 共 {points.length} 个元素</div>
      )}
    </div>
  );
};

type SessionFilters = {
  keyword: string;            // device_id/customer_id/session_id 模糊
  entryPageId: number | 'all';
  exitPageId: number | 'all';
  conversions: Array<'atc' | 'purchase' | 'inquiry' | 'bounce'>;
  durationBuckets: Array<'lt30s' | '30s_5m' | '5m_30m' | 'gt30m'>;
  deviceTypes: DeviceType[];
};

type TrafficAnalysisProps = {
  initialSite?: string;
};

const TrafficAnalysis: React.FC<TrafficAnalysisProps> = ({ initialSite = 'all' }) => {
  const {
    filterSite,
    setFilterSite,
    allowedSiteFilters,
    siteOptions,
    loading: siteAccessLoading,
    error: siteAccessError,
    hasAccess: hasSiteAccess,
  } = useIdpSiteAccess(initialSite);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  // 0 = 未选哨兵；pathflow 到达后吸附到最忙真实页（用户手动选后保留其选择）
  const [selectedFlowPageId, setSelectedFlowPageId] = useState<number>(0);
  const flowPageTouchedRef = useRef(false);
  const [selectedDestFunction, setSelectedDestFunction] = useState<string | undefined>(undefined);
  const [funnelSteps, setFunnelSteps] = useState<FunnelStep[]>(DEFAULT_FUNNEL);
  const [sankeyStart, setSankeyStart] = useState<string>('any'); // 'any' | 'type:HOMEPAGE' | 'page:5'
  const [sankeyMaxDepth, setSankeyMaxDepth] = useState<number>(5);
  const [sankeyTargetPageId, setSankeyTargetPageId] = useState<number | undefined>(undefined); // 目标点：到达 X 的流量从哪来（与起点互斥，target 优先）
  // 热区 tab：URL 直达（onSearch 显式提交）+ 设备桶；URL 非空时优先于页面下拉
  const [heatmapUrl, setHeatmapUrl] = useState<string>('');
  const [heatmapDevice, setHeatmapDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [shotFailed, setShotFailed] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [apiState, setApiState] = useState<TrafficApiState>(emptyApiState);
  // 时间区间（默认近 7 天）；RangeSelect 回吐毫秒时间戳
  const [dateRange, setDateRange] = useState<{ from: number; to: number }>(() => {
    const to = Date.now();
    return { from: to - 7 * 24 * 3600 * 1000, to };
  });
  // 查询条件 = 草稿(filterSite/dateRange/桑基起点等绑输入组件) → 点「查询」快照进 appliedFilters 才驱动取数；
  // 重复点查询 = 强制刷新（对象引用变化触发 effect）
  type AppliedFilters = {
    site: string; from: number; to: number;
    sankeyStart: string; sankeyMaxDepth: number; sankeyTargetPageId?: number;
  };
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>(() => {
    const to = Date.now();
    return { site: initialSite, from: to - 7 * 24 * 3600 * 1000, to, sankeyStart: 'any', sankeyMaxDepth: 5 };
  });

  // 单 session 查询条件
  const [sessionFilters, setSessionFilters] = useState<SessionFilters>({
    keyword: '',
    entryPageId: 'all',
    exitPageId: 'all',
    conversions: [],
    durationBuckets: [],
    deviceTypes: [],
  });
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);

  useEffect(() => {
    if (siteAccessLoading || !filterSite) return;
    const params = new URLSearchParams(window.location.search);
    const selectedWebKeys = webKeysForFilter(filterSite, allowedSiteFilters);
    if (filterSite === 'all' || selectedWebKeys.length !== 1) {
      params.delete('webKey');
    } else {
      params.set('webKey', selectedWebKeys[0]);
    }
    const search = params.toString();
    const nextUrl = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  }, [filterSite, siteAccessLoading, allowedSiteFilters.join(',')]);

  useEffect(() => {
    let cancelled = false;
    if (siteAccessLoading) {
      setApiState(prev => ({
        ...prev,
        loading: true,
        error: undefined,
      }));
      return () => {
        cancelled = true;
      };
    }
    if (!hasSiteAccess) {
      setApiState({
        ...emptyApiState,
        error: siteAccessError || '当前员工没有 FG/ST/BF 独立站渠道权限',
      });
      return () => {
        cancelled = true;
      };
    }
    const to = new Date(appliedFilters.to);
    const from = new Date(appliedFilters.from);
    const baseParams = {
      from: toApiDateTime(from),
      to: toApiDateTime(to),
    };
    const webKeys = webKeysForFilter(appliedFilters.site, allowedSiteFilters);

    setApiState(prev => ({
      ...prev,
      loading: true,
      error: undefined,
    }));

    Promise.all(webKeys.map(async webKey => {
      const errors: string[] = [];
      const [overviewResult, sessionsResult, funnelResult, realtimeResult] = await Promise.all([
        settle(getIdpOverview({ webKey, ...baseParams })),
        settle(getIdpSessions({ webKey, ...baseParams, sessionId: selectedSessionId || undefined })),
        settle(getIdpFunnel({
          webKey,
          ...baseParams,
          steps: ['PAGE_VIEW', 'ADD_TO_CART', 'CHECKOUT_START', 'PURCHASE'],
        })),
        settle(getIdpRealtime({ webKey, limit: Math.max(20, Math.floor(60 / Math.max(webKeys.length, 1))) })),
      ]);
      if (!overviewResult.ok) errors.push(`${webKey} overview: ${errorMessage(overviewResult.reason)}`);
      if (!sessionsResult.ok) errors.push(`${webKey} sessions: ${errorMessage(sessionsResult.reason)}`);
      if (!funnelResult.ok) errors.push(`${webKey} funnel: ${errorMessage(funnelResult.reason)}`);
      if (!realtimeResult.ok) errors.push(`${webKey} realtime: ${errorMessage(realtimeResult.reason)}`);
      return {
        webKey,
        overview: overviewResult.ok ? overviewResult.value : emptyOverview(webKey),
        sessions: sessionsResult.ok ? sessionsResult.value : emptySessions(webKey),
        funnel: funnelResult.ok ? funnelResult.value : emptyFunnel(webKey),
        realtime: realtimeResult.ok ? realtimeResult.value : [],
        errors,
      };
    }))
      .then(async parts => {
        const apiErrors = parts.reduce((acc, part) => acc.concat(part.errors), [] as string[]);
        // 单站类分析（热区/桑基/聚合/cohort）无法跨站合并；"全部站点"时选 PV 最大的站，
        // 避免落到无数据站（如 webKeys[0]=FG 空、数据在 BF）导致打开即空
        const primaryWebKey = parts.slice()
          .sort((a, b) => (b.overview?.pv || 0) - (a.overview?.pv || 0))[0]?.webKey || webKeys[0] || 'BF';
        const [heatmap, pathflow, aggregate, destFunctions, cohortData] = await Promise.all([
          getIdpHeatmap({
            webKey: primaryWebKey,
            pageId: heatmapUrl ? undefined : selectedFlowPageId,
            url: heatmapUrl || undefined,
            deviceBucket: heatmapDevice,
            ...baseParams,
          }).catch(error => {
            apiErrors.push(`${primaryWebKey} heatmap: ${errorMessage(error)}`);
            return undefined;
          }),
          getIdpPathFlow({
            webKey: primaryWebKey,
            ...baseParams,
            // 起点/目标点/深度只影响返回中的桑基段；扁平 nodes/links（页面下拉/路径分析/退出页）后端保持全量
            startPageId: appliedFilters.sankeyStart.startsWith('page:') ? Number(appliedFilters.sankeyStart.slice(5)) : undefined,
            startPageType: appliedFilters.sankeyStart.startsWith('type:') ? appliedFilters.sankeyStart.slice(5) : undefined,
            targetPageId: appliedFilters.sankeyTargetPageId,
            maxDepth: appliedFilters.sankeyMaxDepth,
          }).catch(error => {
            apiErrors.push(`${primaryWebKey} pathflow: ${errorMessage(error)}`);
            return undefined;
          }),
          getIdpAggregate({
            webKey: primaryWebKey,
            ...baseParams,
          }).catch(error => {
            apiErrors.push(`${primaryWebKey} aggregate: ${errorMessage(error)}`);
            return undefined;
          }),
          getIdpDestFunctions({ webKey: primaryWebKey }).catch(error => {
            apiErrors.push(`${primaryWebKey} destfunctions: ${errorMessage(error)}`);
            return undefined;
          }),
          getIdpCohort({ webKey: primaryWebKey, weeks: 8 }).catch(error => {
            apiErrors.push(`${primaryWebKey} cohort: ${errorMessage(error)}`);
            return undefined;
          }),
        ]);

        if (cancelled) return;

        const sessions: ApiSessionSummary[] = [];
        let detail: ApiSessionDetail | undefined;
        parts.forEach(part => {
          (part.sessions.sessions || []).forEach(s => sessions.push({ ...s, webKey: part.webKey }));
          if (part.sessions.detail?.sessionId) {
            detail = { ...part.sessions.detail, webKey: part.webKey };
          }
        });

        setApiState({
          loading: false,
          source: 'api',
          overview: aggregateOverview(parts.map(p => p.overview).filter(Boolean)),
          sessions,
          detail,
          funnel: parts.find(part => part.funnel?.steps?.length)?.funnel || parts[0]?.funnel,
          heatmap,
          realtime: parts.reduce((acc, part) => acc.concat(part.realtime || []), [] as IdpRealtimeEventDTO[]),
          pathflow,
          aggregate,
          destFunctions,
          cohort: cohortData,
          error: apiErrors.length ? `部分 IDP API 失败：${apiErrors.slice(0, 3).join('；')}` : undefined,
        });
      })
      .catch(error => {
        if (cancelled) return;
        setApiState({
          ...emptyApiState,
          error: error?.message || 'IDP analytics API unavailable',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [appliedFilters, selectedSessionId, selectedFlowPageId, heatmapUrl, heatmapDevice, siteAccessLoading, hasSiteAccess, siteAccessError, allowedSiteFilters.join(',')]);

  // 换页/换设备后重试截图（上次失败不粘住新页面）
  useEffect(() => {
    setShotFailed(false);
  }, [apiState.heatmap && apiState.heatmap.pageId, heatmapDevice]);

  // pathflow 到达后：用户没手动选过、或当前页不在真实集合里 → 吸附最忙真实页
  useEffect(() => {
    if (apiState.source !== 'api') return;
    const nodes = apiState.pathflow?.nodes || [];
    if (!nodes.length) return;
    const inSet = nodes.some(n => n.pageId === selectedFlowPageId);
    if (!flowPageTouchedRef.current || !inSet) {
      const busiest = nodes.slice().sort((a, b) => (b.pv || 0) - (a.pv || 0))[0];
      if (busiest && busiest.pageId !== selectedFlowPageId) {
        setSelectedFlowPageId(busiest.pageId);
      }
    }
  }, [apiState.source, apiState.pathflow, selectedFlowPageId]);

  const usingApi = apiState.source === 'api';

  const apiEvents = useMemo(() => (apiState.realtime || []).map(mapApiEvent), [apiState.realtime]);

  const allSessions = useMemo(() => {
    if (usingApi) {
      return (apiState.sessions || []).map(s => mapApiSession(s, apiState.detail));
    }
    return [];
  }, [apiState.sessions, apiState.detail, appliedFilters.site, usingApi]);

  useEffect(() => {
    if (!allSessions.length) return;
    if (!selectedSessionId || !allSessions.some(s => s.session_id === selectedSessionId)) {
      setSelectedSessionId(allSessions[0].session_id);
    }
  }, [allSessions, selectedSessionId]);

  const filteredEvents = useMemo(() => {
    if (usingApi) return apiEvents;
    return [];
  }, [apiEvents, appliedFilters.site, usingApi]);

  // 站点 + 单 session 查询条件过滤
  const filteredSessions = useMemo(() => {
    return allSessions.filter(s => {
      // 关键词:模糊匹配 device_id / customer_id / session_id
      if (sessionFilters.keyword) {
        const kw = sessionFilters.keyword.toLowerCase();
        const hit = s.device_id.toLowerCase().includes(kw)
          || (s.customer_id?.toLowerCase().includes(kw) ?? false)
          || s.session_id.toLowerCase().includes(kw);
        if (!hit) return false;
      }
      if (sessionFilters.entryPageId !== 'all' && s.entry_page_id !== sessionFilters.entryPageId) return false;
      if (sessionFilters.exitPageId !== 'all' && s.exit_page_id !== sessionFilters.exitPageId) return false;
      if (sessionFilters.deviceTypes.length > 0 && !sessionFilters.deviceTypes.includes(s.device_type)) return false;
      // 转化筛(任一命中即可)
      if (sessionFilters.conversions.length > 0) {
        const matchConversion = sessionFilters.conversions.some(c => {
          if (c === 'atc') return s.has_atc;
          if (c === 'purchase') return s.has_purchase;
          if (c === 'inquiry') return s.has_inquiry;
          if (c === 'bounce') return s.page_view_count <= 1;
          return false;
        });
        if (!matchConversion) return false;
      }
      // 时长区间(任一命中)
      if (sessionFilters.durationBuckets.length > 0) {
        const sec = s.duration_ms / 1000;
        const matchDuration = sessionFilters.durationBuckets.some(b => {
          if (b === 'lt30s') return sec < 30;
          if (b === '30s_5m') return sec >= 30 && sec < 300;
          if (b === '5m_30m') return sec >= 300 && sec < 1800;
          if (b === 'gt30m') return sec >= 1800;
          return false;
        });
        if (!matchDuration) return false;
      }
      return true;
    });
  }, [allSessions, sessionFilters]);

  const resetSessionFilters = () => setSessionFilters({
    keyword: '', entryPageId: 'all', exitPageId: 'all',
    conversions: [], durationBuckets: [], deviceTypes: [],
  });
  const activeFilterCount =
    (sessionFilters.keyword ? 1 : 0) +
    (sessionFilters.entryPageId !== 'all' ? 1 : 0) +
    (sessionFilters.exitPageId !== 'all' ? 1 : 0) +
    sessionFilters.conversions.length +
    sessionFilters.durationBuckets.length +
    sessionFilters.deviceTypes.length;

  // 三层身份计数
  const apiIdentity = sumDimension(apiState.overview?.identityDistribution);
  const uniqueDevices = usingApi ? (apiState.overview?.uv || 0) : new Set(filteredEvents.map(e => e.device_id)).size;
  const totalPv = usingApi ? (apiState.overview?.pv || 0) : filteredEvents.filter(e => e.event_type === 'page_view').length;
  const uniqueSessions = usingApi ? allSessions.length : new Set(filteredEvents.map(e => e.session_id)).size;
  const uniqueCustomers = usingApi ? (apiIdentity.customer || 0) : new Set(filteredEvents.filter(e => e.customer_id).map(e => e.customer_id)).size;
  const customerCoverage = uniqueDevices > 0 ? uniqueCustomers / uniqueDevices : 0;

  // 设备分布
  const deviceStats = useMemo(() => {
    const m: Record<DeviceType, number> = { mobile: 0, desktop: 0, tablet: 0 };
    if (usingApi && apiState.overview?.deviceDistribution?.length) {
      apiState.overview.deviceDistribution.forEach(item => {
        const key = normalizeDeviceType(item.name);
        m[key] += item.value || 0;
      });
      return m;
    }
    filteredSessions.forEach(s => m[s.device_type]++);
    return m;
  }, [apiState.overview, filteredSessions, usingApi]);

  // 事件类型分布
  const eventTypeStats = useMemo(() => {
    const map: Partial<Record<EventType, number>> = {};
    if (usingApi && apiState.overview?.eventTypeDistribution?.length) {
      apiState.overview.eventTypeDistribution.forEach(item => {
        const key = normalizeEventType(item.name);
        map[key] = (map[key] || 0) + (item.value || 0);
      });
      return map;
    }
    filteredEvents.forEach(e => { map[e.event_type] = (map[e.event_type] || 0) + 1; });
    return map;
  }, [apiState.overview, filteredEvents, usingApi]);
  const eventTypeTotal = Math.max(Object.values(eventTypeStats).reduce((s, n) => s + (n || 0), 0), 1);

  // DAU/WAU/MAU
  const dauWauMau = useMemo(() => {
    if (usingApi && apiState.overview?.trend?.length) {
      return {
        dau: apiState.overview.trend.map(d => ({ date: d.day, users: d.uv || 0 })),
        wau: apiState.overview.wau || 0,
        mau: apiState.overview.mau || 0,
      };
    }
    return { dau: [] as Array<{ date: string; users: number }>, wau: 0, mau: 0 };
  }, [apiState.overview, usingApi]);

  // 选中的 session 详情
  const selectedSession = allSessions.find(s => s.session_id === selectedSessionId);
  const selectedSessionEvents = useMemo(() => {
    if (usingApi && apiState.detail?.sessionId === selectedSessionId) {
      return (apiState.detail.events || []).map(mapApiEvent);
    }
    return [];
  }, [apiState.detail, selectedSessionId, usingApi]);

  // 路径分析（从某页出发）：API 模式用 pathflow.links 实链路，mock 兜底
  const pathFlow = useMemo(() => {
    if (usingApi && apiState.pathflow?.links?.length) {
      const nodeById = new Map((apiState.pathflow.nodes || []).map(n => [n.pageId, n]));
      const links = apiState.pathflow.links.filter(l => l.sourcePageId === selectedFlowPageId);
      const total = links.reduce((s, l) => s + (l.value || 0), 0) || 1;
      return links
        .slice()
        .sort((a, b) => (b.value || 0) - (a.value || 0))
        .slice(0, 8)
        .map(l => ({
          next_page_id: l.targetPageId,
          next_title: pageNodeLabel(nodeById.get(l.targetPageId)) || `page#${l.targetPageId}`,
          count: l.value || 0,
          pct: (l.value || 0) / total,
        }));
    }
    return [];
  }, [usingApi, apiState.pathflow, selectedFlowPageId]);
  const exitPages = useMemo(() => {
    if (usingApi && apiState.pathflow?.nodes?.length) {
      const totalExits = apiState.pathflow.nodes.reduce((s, n) => s + (n.exits || 0), 0) || 1;
      return apiState.pathflow.nodes
        .filter(n => (n.exits || 0) > 0)
        .slice()
        .sort((a, b) => (b.exits || 0) - (a.exits || 0))
        .slice(0, 8)
        .map(n => ({ page_id: n.pageId, title: pageNodeLabel(n), exits: n.exits || 0, pct: (n.exits || 0) / totalExits }));
    }
    return [];
  }, [usingApi, apiState.pathflow]);
  // 起点页/热力页下拉：API 模式用 pathflow.nodes 真实页（按 PV 降序），mock 兜底
  const pageOptions = useMemo(() => {
    if (usingApi && apiState.pathflow?.nodes?.length) {
      return apiState.pathflow.nodes
        .slice()
        .sort((a, b) => (b.pv || 0) - (a.pv || 0))
        .map(n => ({ id: n.pageId, title: `${pageNodeLabel(n)} · ${n.pv || 0} PV` }));
    }
    return [] as Array<{ id: number; title: string }>;
  }, [usingApi, apiState.pathflow]);

  // 漏斗（直线版,保留作 fallback）
  const calculatedFunnel = useMemo(() => {
    if (usingApi && apiState.funnel?.steps?.length) {
      return apiState.funnel.steps.map((s, i) => ({
        step_no: s.stepIndex || i + 1,
        name: eventLabel(s.stepKey || s.label),
        match: { event_type: normalizeEventType(s.stepKey || s.label) },
        user_count: s.users || 0,
        drop_off_pct: s.dropOffRate || 0,
      }));
    }
    return [];
  }, [apiState.funnel, funnelSteps, usingApi]);
  const funnelMax = calculatedFunnel[0]?.user_count || 1;

  // 桑基图数据
  const sankeyData = useMemo(() => {
    if (usingApi && apiState.pathflow?.sankeyNodes?.length) {
      // 后端 pathFlow 已做 per-session 精确分层（depth+pageType+EXIT+action 节点），直接用
      return {
        nodes: apiState.pathflow.sankeyNodes,
        links: apiState.pathflow.sankeyLinks || [],
        totalStart: apiState.pathflow.sankeyTotalStart || 0,
      };
    }
    return { nodes: [], links: [], totalStart: 0 };
  }, [sankeyStart, sankeyMaxDepth, usingApi, apiState.pathflow]);

  // Cohort：API 真数据（周锚定），未满 N 天的格子 = null → 灰格；mock 兜底
  const cohorts = useMemo(() => {
    if (usingApi && apiState.cohort?.cohorts?.length) {
      return apiState.cohort.cohorts.map(c => ({
        cohort_date: c.cohortDate,
        cohort_size: c.cohortSize || 0,
        retention: {
          d0: c.d0 ?? null, d1: c.d1 ?? null, d3: c.d3 ?? null,
          d7: c.d7 ?? null, d14: c.d14 ?? null, d30: c.d30 ?? null,
        },
      }));
    }
    return [];
  }, [usingApi, apiState.cohort]);

  // 热区
  const viewportHeatmap = useMemo(() => {
    if (usingApi && apiState.heatmap?.clicks?.length) {
      const maxClicks = Math.max(...apiState.heatmap.clicks.map(h => h.clicks || 0), 1);
      const points = apiState.heatmap.clicks
        .filter(h => h.xPct !== undefined && h.yPct !== undefined)
        .map(h => ({
          x: h.xPct || 0,
          y: h.yPct || 0,
          intensity: Math.max(0.15, (h.clicks || 0) / maxClicks),
          selector: h.elementSelector,
          elementText: h.elementText,
          clicks: h.clicks || 0,
          uniqueUsers: h.uniqueUsers || 0,
        }));
      return { mobile: points, desktop: points, tablet: [] };
    }
    return { mobile: [], desktop: [], tablet: [] };
  }, [apiState.heatmap, usingApi]);
  const scrollReadRate = useMemo(() => {
    if (usingApi && apiState.heatmap?.scrollBuckets?.length) {
      const total = apiState.heatmap.scrollBuckets.reduce((s, b) => s + (b.users || 0), 0) || 1;
      const reached100 = apiState.heatmap.scrollBuckets.find(b => b.bucket >= 100)?.users || 0;
      const topBounce = apiState.heatmap.scrollBuckets.find(b => b.bucket <= 25)?.users || 0;
      return [{
        page_id: apiState.heatmap.pageId,
        title: pageNodeLabel({ pageId: apiState.heatmap.pageId, url: apiState.heatmap.pageUrl }),
        url: apiState.heatmap.pageUrl || '',
        reached_100_pct: reached100 / total,
        topBounce_pct: topBounce / total,
      }];
    }
    return [];
  }, [apiState.heatmap, usingApi]);

  // 聚合统计（destFunction 维度，客户端按 elementText/模块 过滤已拉全量）
  const [selectedModule, setSelectedModule] = useState<string | undefined>(undefined);
  const aggregateRows = useMemo(() => {
    let rows = (usingApi ? apiState.aggregate?.rows : undefined) || [];
    if (selectedModule) rows = rows.filter(r => moduleOf(r.elementText || r.selector) === selectedModule);
    if (selectedDestFunction) rows = rows.filter(r => r.elementText === selectedDestFunction);
    return rows;
  }, [usingApi, apiState.aggregate, selectedDestFunction, selectedModule]);
  // 默认按 destFunction label 汇总：pv 求和；uv 跨行不可去重相加，取组内最大值（下界口径）
  const aggregateGroups = useMemo(() => {
    const byLabel = new Map<string, { label: string; module: string; pv: number; uv: number; rows: IdpAggregateRow[] }>();
    aggregateRows.forEach(r => {
      const label = r.elementText || r.selector || 'unknown';
      const g = byLabel.get(label) || { label, module: moduleOf(label), pv: 0, uv: 0, rows: [] };
      g.pv += r.pv || 0;
      g.uv = Math.max(g.uv, r.uv || 0);
      g.rows.push(r);
      byLabel.set(label, g);
    });
    return Array.from(byLabel.values()).sort((a, b) => b.pv - a.pv);
  }, [aggregateRows]);
  const moduleOptions = useMemo(() => {
    const all = (usingApi ? apiState.aggregate?.rows : undefined) || [];
    return Array.from(new Set(all.map(r => moduleOf(r.elementText || r.selector)))).sort();
  }, [usingApi, apiState.aggregate]);

  // 总览趋势：指标(日活/PV/点击) × 粒度(按日/按月)。月 UV 跨日不可加，取日均并在 UI 标注口径；PV/点击按月求和
  const [trendMetric, setTrendMetric] = useState<'uv' | 'pv' | 'clicks'>('uv');
  const [trendGranularity, setTrendGranularity] = useState<'day' | 'month'>('day');
  const trendSeries = useMemo(() => {
    const daily = usingApi && apiState.overview?.trend?.length
      ? apiState.overview.trend.map(d => ({ date: String(d.day || ''), uv: d.uv || 0, pv: d.pv || 0, clicks: d.clicks || 0 }))
      : dauWauMau.dau.map(d => ({ date: d.date, uv: d.users, pv: 0, clicks: 0 }));
    if (trendGranularity === 'day') {
      return daily.map(d => ({ label: d.date.slice(5), full: d.date, value: d[trendMetric] }));
    }
    const byMonth = new Map<string, { sum: number; days: number }>();
    daily.forEach(d => {
      const m = d.date.slice(0, 7);
      const cur = byMonth.get(m) || { sum: 0, days: 0 };
      cur.sum += d[trendMetric];
      cur.days += 1;
      byMonth.set(m, cur);
    });
    return Array.from(byMonth.entries()).map(([m, v]) => ({
      label: m,
      full: m,
      value: trendMetric === 'uv' ? Math.round(v.sum / Math.max(v.days, 1)) : v.sum,
    }));
  }, [usingApi, apiState.overview, dauWauMau, trendMetric, trendGranularity]);

  // 单 session timeline
  const sessionTimelineColor = (et: EventType) => ({
    page_view: 'blue', click: 'cyan', scroll: '#bfbfbf',
    add_to_cart: 'orange', checkout_start: 'gold', purchase: 'green', inquiry_open: 'magenta',
  } as Record<EventType, string>)[et];

  // 筛选贴数据（每个 tab 顶部渲染同一份）：站点/时间改的是草稿，点「查询」快照进 appliedFilters 才生效
  const filterBar = (
    <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: '10px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ whiteSpace: 'nowrap' }}>站点:</span>
          <Select
            value={filterSite || undefined}
            onChange={setFilterSite}
            style={{ width: 120 }}
            loading={siteAccessLoading}
            disabled={siteAccessLoading || !hasSiteAccess}
            placeholder="无权限"
          >
            {siteOptions.length > 1 ? <Select.Option value="all">全部</Select.Option> : null}
            {siteOptions.map(site => (
              <Select.Option key={site.siteId} value={site.siteId}>{site.label}</Select.Option>
            ))}
          </Select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ whiteSpace: 'nowrap' }}>时间:</span>
          <RangeSelect
            value={dateRange}
            startName="from"
            endName="to"
            isThreeMonth
            onChange={(v: any) => setDateRange(v || { from: Date.now() - 7 * 24 * 3600 * 1000, to: Date.now() })}
          />
        </div>
        <Button
          type="primary"
          loading={apiState.loading}
          disabled={siteAccessLoading || !hasSiteAccess}
          onClick={() => setAppliedFilters({
            site: filterSite || 'all',
            from: dateRange.from,
            to: dateRange.to,
            sankeyStart,
            sankeyMaxDepth,
            sankeyTargetPageId,
          })}
        >
          查 询
        </Button>
        {apiState.error ? (
          <span style={{ fontSize: 11, color: '#cf1322', marginLeft: 'auto' }}>{apiState.error}</span>
        ) : null}
      </div>
    </Card>
  );

  return (
    <div style={{ padding: '16px 24px' }}>
      {!hasSiteAccess && !siteAccessLoading ? (
        <Alert
          type="warning"
          showIcon
          message="当前员工没有 FG/ST/BF 独立站渠道权限"
          style={{ marginBottom: 12 }}
        />
      ) : null}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          // ============================================================
          // Tab 1：总览
          // ============================================================
          {
            key: 'overview',
            label: '① 总览',
            children: (
              <>
                {filterBar}

                {/* KPI 7 项 */}
                <Row gutter={12} style={{ marginBottom: 16 }}>
                  <Col span={3}><Card size="small"><Statistic title="UV" value={uniqueDevices} prefix={<MobileOutlined />} /></Card></Col>
                  <Col span={3}><Card size="small"><Statistic title="PV" value={totalPv} valueStyle={{ color: '#13c2c2' }} /></Card></Col>
                  <Col span={3}><Card size="small"><Statistic title="会话数" value={uniqueSessions} valueStyle={{ color: '#1890ff' }} /></Card></Col>
                  <Col span={3}><Card size="small"><Statistic title="登录用户数" value={uniqueCustomers} valueStyle={{ color: '#52c41a' }} /></Card></Col>
                  <Col span={3}><Card size="small"><Statistic title="登录覆盖率" value={(customerCoverage * 100).toFixed(1)} suffix="%" valueStyle={{ color: customerCoverage > 0.35 ? '#52c41a' : '#fa8c16' }} /></Card></Col>
                  <Col span={3}><Card size="small"><Statistic title="日活用户（今日）" value={dauWauMau.dau[dauWauMau.dau.length - 1]?.users || 0} /></Card></Col>
                  <Col span={3}><Card size="small"><Statistic title="月活用户（近30天）" value={dauWauMau.mau} valueStyle={{ color: '#722ed1' }} /></Card></Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    <Card
                      title="流量趋势"
                      size="small"
                      extra={
                        <Space size={8}>
                          <Select size="small" value={trendMetric} onChange={setTrendMetric} style={{ width: 90 }}>
                            <Select.Option value="uv">日活/UV</Select.Option>
                            <Select.Option value="pv">PV</Select.Option>
                            <Select.Option value="clicks">点击</Select.Option>
                          </Select>
                          <Select size="small" value={trendGranularity} onChange={setTrendGranularity} style={{ width: 78 }}>
                            <Select.Option value="day">按日</Select.Option>
                            <Select.Option value="month">按月</Select.Option>
                          </Select>
                        </Space>
                      }
                    >
                      {trendGranularity === 'month' && trendMetric === 'uv' ? (
                        <div style={{ fontSize: 10, color: '#8c8c8c', marginBottom: 4 }}>月度口径 = 日均日活（跨日去重不可直接相加）</div>
                      ) : null}
                      <div style={{ display: 'flex', alignItems: 'flex-end', height: 140, gap: 4, padding: '8px 0' }}>
                        {trendSeries.map(d => {
                          const max = Math.max(...trendSeries.map(x => x.value), 1);
                          const h = (d.value / max) * 100;
                          return (
                            <Tooltip key={d.full} title={`${d.full}: ${d.value}`}>
                              <div style={{ flex: 1, maxWidth: 48, background: '#1890ff', height: `${h}%`, borderRadius: '2px 2px 0 0', minHeight: 4 }} />
                            </Tooltip>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: 4, fontSize: 9, color: '#8c8c8c', marginTop: 4 }}>
                        {trendSeries.map(d => <div key={d.full} style={{ flex: 1, maxWidth: 48, textAlign: 'center', overflow: 'hidden' }}>{d.label}</div>)}
                      </div>
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card title="设备分布（session 维度）" size="small">
                      {(['mobile', 'desktop', 'tablet'] as DeviceType[]).map(dt => {
                        const total = Object.values(deviceStats).reduce((s, n) => s + n, 0);
                        const pct = total > 0 ? deviceStats[dt] / total * 100 : 0;
                        return (
                          <div key={dt} style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                              <span>{DEVICE_ICONS[dt]} {dt}</span>
                              <span>{deviceStats[dt]} ({pct.toFixed(0)}%)</span>
                            </div>
                            <Progress percent={pct} showInfo={false} strokeColor={dt === 'mobile' ? '#1890ff' : dt === 'desktop' ? '#52c41a' : '#fa8c16'} />
                          </div>
                        );
                      })}
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card title="事件类型分布" size="small">
                      {Object.entries(eventTypeStats).sort((a, b) => (b[1] as number) - (a[1] as number)).map(([t, n]) => {
                        const pct = ((n as number) / eventTypeTotal * 100);
                        return (
                          <div key={t} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                            <Tag color={EVENT_TYPE_COLORS[t as EventType]}>{EVENT_TYPE_LABELS[t as EventType]}</Tag>
                            <span>{n} <small style={{ color: '#8c8c8c' }}>({pct.toFixed(1)}%)</small></span>
                          </div>
                        );
                      })}
                    </Card>
                  </Col>
                </Row>
              </>
            ),
          },

          // ============================================================
          // Tab 2：会话与路径
          // ============================================================
          {
            key: 'session',
            label: '② 会话与路径',
            children: (
              <>
                {filterBar}

                {/* 会话查询条 */}
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Row gutter={8} align="middle">
                    <Col flex="240px">
                      <Input.Search
                        size="small"
                        placeholder="device / customer / session id 模糊"
                        value={sessionFilters.keyword}
                        onChange={e => setSessionFilters(f => ({ ...f, keyword: e.target.value }))}
                        allowClear
                      />
                    </Col>
                    <Col>
                      <span style={{ fontSize: 11, marginRight: 4 }}>转化:</span>
                      <Select
                        size="small"
                        mode="multiple"
                        placeholder="任意"
                        style={{ width: 180 }}
                        value={sessionFilters.conversions}
                        onChange={v => setSessionFilters(f => ({ ...f, conversions: v }))}
                        maxTagCount={2}
                      >
                        <Select.Option value="atc">含加购</Select.Option>
                        <Select.Option value="purchase">含购买</Select.Option>
                        <Select.Option value="inquiry">含询单</Select.Option>
                        <Select.Option value="bounce">跳出(PV=1)</Select.Option>
                      </Select>
                    </Col>
                    <Col>
                      <span style={{ fontSize: 11, marginRight: 4 }}>设备:</span>
                      <Select
                        size="small"
                        mode="multiple"
                        placeholder="全部"
                        style={{ width: 160 }}
                        value={sessionFilters.deviceTypes}
                        onChange={v => setSessionFilters(f => ({ ...f, deviceTypes: v }))}
                        maxTagCount={3}
                      >
                        <Select.Option value="mobile">mobile</Select.Option>
                        <Select.Option value="desktop">desktop</Select.Option>
                        <Select.Option value="tablet">tablet</Select.Option>
                      </Select>
                    </Col>
                    <Col>
                      <Button size="small" onClick={() => setShowAdvancedFilter(s => !s)}>
                        {showAdvancedFilter ? '收起' : '高级'}{activeFilterCount > 0 && <Tag color="blue" style={{ marginLeft: 4 }}>{activeFilterCount}</Tag>}
                      </Button>
                    </Col>
                    {activeFilterCount > 0 && (
                      <Col>
                        <Button size="small" type="link" onClick={resetSessionFilters}>清空</Button>
                      </Col>
                    )}
                    <Col flex="auto" style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                        命中 <strong>{filteredSessions.length}</strong> / 全部 {allSessions.length} 会话
                      </span>
                    </Col>
                  </Row>
                  {showAdvancedFilter && (
                    <Row gutter={8} align="middle" style={{ marginTop: 8 }}>
                      <Col>
                        <span style={{ fontSize: 11, marginRight: 4 }}>入口页:</span>
                        <Select
                          size="small"
                          showSearch
                          style={{ width: 180 }}
                          value={sessionFilters.entryPageId}
                          onChange={v => setSessionFilters(f => ({ ...f, entryPageId: v }))}
                          optionFilterProp="children"
                        >
                          <Select.Option value="all">全部</Select.Option>
                          {pageOptions.map(p => (
                            <Select.Option key={p.id} value={p.id}>{p.title}</Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col>
                        <span style={{ fontSize: 11, marginRight: 4 }}>退出页:</span>
                        <Select
                          size="small"
                          showSearch
                          style={{ width: 180 }}
                          value={sessionFilters.exitPageId}
                          onChange={v => setSessionFilters(f => ({ ...f, exitPageId: v }))}
                          optionFilterProp="children"
                        >
                          <Select.Option value="all">全部</Select.Option>
                          {pageOptions.map(p => (
                            <Select.Option key={p.id} value={p.id}>{p.title}</Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col>
                        <span style={{ fontSize: 11, marginRight: 4 }}>时长:</span>
                        <Select
                          size="small"
                          mode="multiple"
                          placeholder="全部"
                          style={{ width: 240 }}
                          value={sessionFilters.durationBuckets}
                          onChange={v => setSessionFilters(f => ({ ...f, durationBuckets: v }))}
                          maxTagCount={4}
                        >
                          <Select.Option value="lt30s">&lt; 30s</Select.Option>
                          <Select.Option value="30s_5m">30s - 5min</Select.Option>
                          <Select.Option value="5m_30m">5 - 30min</Select.Option>
                          <Select.Option value="gt30m">&gt; 30min</Select.Option>
                        </Select>
                      </Col>
                    </Row>
                  )}
                </Card>

                <Row gutter={12}>
                  {/* 左：session 列表 */}
                  <Col span={7}>
                    <Card size="small" title={`会话列表 (${filteredSessions.length})`} bodyStyle={{ padding: 0, maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
                      {filteredSessions.slice(0, 50).map(s => (
                        <div
                          key={s.session_id}
                          onClick={() => setSelectedSessionId(s.session_id)}
                          style={{
                            padding: 10, borderBottom: '1px solid #f5f5f5', cursor: 'pointer',
                            background: selectedSessionId === s.session_id ? '#e6f7ff' : undefined,
                            borderLeft: selectedSessionId === s.session_id ? '3px solid #1890ff' : '3px solid transparent',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                            <span style={{ fontFamily: 'monospace' }}>{s.session_id}</span>
                            <span>{DEVICE_ICONS[s.device_type]} {SITE_NAMES[s.site_id]}</span>
                          </div>
                          <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>
                            {s.device_id} {s.customer_id ? `· 已登录` : '· 匿名'}
                          </div>
                          <div style={{ fontSize: 11, marginTop: 4 }}>
                            <Tag color="blue" style={{ fontSize: 10 }}>{s.page_view_count} PV</Tag>
                            {s.has_atc && <Tag color="orange" style={{ fontSize: 10 }}>加购</Tag>}
                            {s.has_purchase && <Tag color="green" style={{ fontSize: 10 }}>购买</Tag>}
                            {s.has_inquiry && <Tag color="magenta" style={{ fontSize: 10 }}>询单</Tag>}
                          </div>
                          <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 4 }}>
                            <ClockCircleOutlined /> {Math.round(s.duration_ms / 60000)}min · {new Date(s.start_at).toLocaleString('zh-CN')}
                          </div>
                        </div>
                      ))}
                    </Card>
                  </Col>

                  {/* 中：单 session 事件链 */}
                  <Col span={10}>
                    <Card size="small" title={`session 事件链：${selectedSessionId || '未选'}`} style={{ maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
                      {selectedSession ? (
                        <>
                          <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 12 }}>
                            <div>device_id: <code>{selectedSession.device_id}</code></div>
                            <div>customer_id: <code>{selectedSession.customer_id || '(匿名)'}</code></div>
                            <div>device: {selectedSession.device_type} · site: {SITE_NAMES[selectedSession.site_id]}</div>
                            <div>入口: {selectedSession.entry_page_url} · 退出: {selectedSession.exit_page_url}</div>
                          </div>
                          <Timeline mode="left">
                            {selectedSessionEvents.map(e => (
                              <Timeline.Item
                                key={e.id}
                                color={sessionTimelineColor(e.event_type)}
                                label={<span style={{ fontSize: 10 }}>{new Date(e.event_time).toLocaleTimeString('zh-CN')}</span>}
                              >
                                <div style={{ fontSize: 12 }}>
                                  <Tag color={EVENT_TYPE_COLORS[e.event_type]}>{EVENT_TYPE_LABELS[e.event_type]}</Tag>
                                  {e.page_id && (
                                    <span>{String(e.props?.pageUrl || '')}</span>
                                  )}
                                  {e.event_type === 'click' && e.element_id && (
                                    <code style={{ fontSize: 10, marginLeft: 8 }}>
                                      {String(e.props?.elementText || '')}
                                    </code>
                                  )}
                                  {e.engagement_ms && (
                                    <small style={{ color: '#8c8c8c', marginLeft: 8 }}>{e.engagement_ms}ms 停留</small>
                                  )}
                                  {e.scroll_depth && (
                                    <Tag style={{ fontSize: 10, marginLeft: 8 }}>滚至 {e.scroll_depth}%</Tag>
                                  )}
                                </div>
                              </Timeline.Item>
                            ))}
                          </Timeline>
                        </>
                      ) : <Empty description="左侧选一个会话" />}
                    </Card>
                  </Col>

                  {/* 右：路径分析 + 退出页 + 跨站点 device */}
                  <Col span={7}>
                    <Card size="small" title="路径分析（从 X 页用户去了 Top N）" style={{ marginBottom: 12 }}>
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 11, marginRight: 4 }}>起点页:</span>
                        <Select
                          value={selectedFlowPageId}
                          onChange={(v) => { flowPageTouchedRef.current = true; setSelectedFlowPageId(v); }}
                          style={{ width: 220 }}
                          size="small"
                          showSearch
                          optionFilterProp="children"
                        >
                          {pageOptions.map(p => (
                            <Select.Option key={p.id} value={p.id}>{p.title}</Select.Option>
                          ))}
                        </Select>
                      </div>
                      {pathFlow.length === 0 ? <Empty description="无后续路径" /> : pathFlow.map(p => (
                        <div key={p.next_page_id} style={{ marginBottom: 6 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                            <span>→ {p.next_title}</span>
                            <span>{p.count} ({(p.pct * 100).toFixed(0)}%)</span>
                          </div>
                          <Progress percent={p.pct * 100} showInfo={false} size="small" />
                        </div>
                      ))}
                    </Card>

                    <Card size="small" title="退出页 Top 8" style={{ marginBottom: 12 }}>
                      {exitPages.map(e => (
                        <div key={e.page_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                          <span>{e.title}</span>
                          <span><strong>{e.exits}</strong> <small style={{ color: '#cf1322' }}>({(e.pct * 100).toFixed(0)}%)</small></span>
                        </div>
                      ))}
                    </Card>

                  </Col>
                </Row>
              </>
            ),
          },

          // ============================================================
          // Tab 3：漏斗与留存
          // ============================================================
          {
            key: 'funnel',
            label: '③ 路径与留存',
            children: (
              <>
                {filterBar}

                <Card size="small" style={{ marginBottom: 12 }} title="用户路径桑基图（起点 → 多步分流）" extra={
                  <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                    选起点 → 看后续 {sankeyMaxDepth} 步分流。ribbon 宽 = 流量；hover 看具体数；流向"退出"=该步用户离开
                  </span>
                }>
                  {/* 起点选 + 深度选 */}
                  <Row gutter={8} align="middle" style={{ marginBottom: 12 }}>
                    <Col><span style={{ fontSize: 11 }}>起点:</span></Col>
                    <Col>
                      <Select
                        size="small"
                        value={sankeyStart}
                        onChange={setSankeyStart}
                        style={{ width: 240 }}
                        showSearch
                        optionFilterProp="children"
                      >
                        <Select.OptGroup label="按入口类型">
                          <Select.Option value="any">🌐 任意入口</Select.Option>
                          <Select.Option value="type:HOMEPAGE">🏠 任意首页入</Select.Option>
                          <Select.Option value="type:COLLECTION">📑 任意 collection 入</Select.Option>
                          <Select.Option value="type:PDP">💍 任意 PDP 入（直达）</Select.Option>
                          <Select.Option value="type:BLOG">📝 任意博客入</Select.Option>
                        </Select.OptGroup>
                        <Select.OptGroup label="按具体页面">
                          {pageOptions.map(p => (
                            <Select.Option key={p.id} value={`page:${p.id}`}>{p.title}</Select.Option>
                          ))}
                        </Select.OptGroup>
                      </Select>
                    </Col>
                    <Col style={{ marginLeft: 16 }}><span style={{ fontSize: 11 }}>目标点:</span></Col>
                    <Col>
                      <Select
                        size="small"
                        value={sankeyTargetPageId}
                        onChange={setSankeyTargetPageId}
                        style={{ width: 220 }}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        placeholder="无（看起点分流）"
                      >
                        {pageOptions.map(p => (
                          <Select.Option key={p.id} value={p.id}>{p.title}</Select.Option>
                        ))}
                      </Select>
                    </Col>
                    <Col style={{ marginLeft: 16 }}><span style={{ fontSize: 11 }}>深度:</span></Col>
                    <Col>
                      <Select
                        size="small"
                        value={sankeyMaxDepth}
                        onChange={setSankeyMaxDepth}
                        style={{ width: 100 }}
                      >
                        {[3, 4, 5, 6, 7].map(n => <Select.Option key={n} value={n}>{n} 步</Select.Option>)}
                      </Select>
                    </Col>
                    {sankeyTargetPageId ? (
                      <Col><span style={{ fontSize: 11, color: '#8c8c8c' }}>目标点模式：展示到达该页的来源路径，起点选择不生效</span></Col>
                    ) : null}
                    <Col flex="auto" style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                        命中 <strong>{sankeyData.totalStart}</strong> 个 session 起点
                      </span>
                    </Col>
                  </Row>

                  {/* 桑基图 */}
                  <div style={{ overflowX: 'auto' }}>
                    <SankeyChart data={sankeyData} width={Math.max(900, sankeyMaxDepth * 200)} height={460} />
                  </div>

                  {/* 节点类型 legend */}
                  <div style={{ marginTop: 8, fontSize: 11, color: '#595959', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {[
                      { type: 'HOMEPAGE', label: '首页', color: '#1890ff' },
                      { type: 'COLLECTION', label: 'Collection', color: '#13c2c2' },
                      { type: 'PDP', label: 'PDP', color: '#722ed1' },
                      { type: 'CART', label: '购物车', color: '#fa8c16' },
                      { type: 'CHECKOUT', label: '结账', color: '#eb2f96' },
                      { type: 'ACTION', label: '动作(加购/购/询)', color: '#fa541c' },
                      { type: 'EXIT', label: '退出', color: '#8c8c8c' },
                    ].map(t => (
                      <span key={t.type}>
                        <span style={{ display: 'inline-block', width: 12, height: 8, background: t.color, marginRight: 4, verticalAlign: 'middle' }} />
                        {t.label}
                      </span>
                    ))}
                  </div>
                </Card>

                <Row gutter={12}>
                  <Col span={12}>
                    <Card size="small" title="多步自定义漏斗（直线，按 device 去重）" extra={
                      <Button size="small" onClick={() => setFunnelSteps(DEFAULT_FUNNEL)}>重置</Button>
                    }>
                      {calculatedFunnel.map((s, i) => {
                        const widthPct = (s.user_count / funnelMax) * 100;
                        return (
                          <div key={s.step_no} style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                              <span>Step {s.step_no}：{s.name}</span>
                              <span>
                                <strong>{s.user_count}</strong> users
                                {i > 0 && (
                                  <Tag color={s.drop_off_pct > 0.5 ? 'red' : s.drop_off_pct > 0.3 ? 'orange' : 'green'} style={{ marginLeft: 8 }}>
                                    -{(s.drop_off_pct * 100).toFixed(1)}%
                                  </Tag>
                                )}
                              </span>
                            </div>
                            <div style={{ position: 'relative', height: 24, background: '#fafafa', borderRadius: 3, marginTop: 4 }}>
                              <div style={{
                                width: `${widthPct}%`, height: '100%',
                                background: `linear-gradient(90deg, #1890ff, ${i === 0 ? '#1890ff' : i === calculatedFunnel.length - 1 ? '#52c41a' : '#69c0ff'})`,
                                borderRadius: 3, transition: 'width 0.3s',
                              }} />
                            </div>
                            <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>
                              match: {s.match.event_type ? `event=${s.match.event_type}` : `page=${s.match.page_type}`}
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 8 }}>
                        端到端转化率: <strong>{(calculatedFunnel.length ? calculatedFunnel[calculatedFunnel.length - 1].user_count / (calculatedFunnel[0].user_count || 1) * 100 : 0).toFixed(2)}%</strong>
                      </div>
                    </Card>
                  </Col>

                  <Col span={12}>
                    <Card size="small" title="Cohort 留存矩阵（device_id 维度）">
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <th style={{ padding: 4, textAlign: 'left' }}>Cohort（首次访问周）</th>
                            <th style={{ padding: 4, textAlign: 'right' }}>size</th>
                            <th style={{ padding: 4, textAlign: 'center' }}>d1</th>
                            <th style={{ padding: 4, textAlign: 'center' }}>d3</th>
                            <th style={{ padding: 4, textAlign: 'center' }}>d7</th>
                            <th style={{ padding: 4, textAlign: 'center' }}>d14</th>
                            <th style={{ padding: 4, textAlign: 'center' }}>d30</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cohorts.map(c => {
                            const cell = (v: number | null) => {
                              if (v === null || v === undefined) {
                                return <td style={{ padding: 4, textAlign: 'center', background: '#fafafa', color: '#bfbfbf', fontSize: 10, border: '1px solid #fff' }}>–</td>;
                              }
                              const intensity = v;
                              const bg = `rgba(24, 144, 255, ${intensity})`;
                              const fg = intensity > 0.15 ? '#fff' : '#262626';
                              return <td style={{ padding: 4, textAlign: 'center', background: bg, color: fg, fontSize: 10, border: '1px solid #fff' }}>{(v * 100).toFixed(1)}%</td>;
                            };
                            return (
                              <tr key={c.cohort_date}>
                                <td style={{ padding: 4 }}>{c.cohort_date}</td>
                                <td style={{ padding: 4, textAlign: 'right' }}>{c.cohort_size}</td>
                                {cell(c.retention.d1)}
                                {cell(c.retention.d3)}
                                {cell(c.retention.d7)}
                                {cell(c.retention.d14)}
                                {cell(c.retention.d30)}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 8 }}>
                        珠宝独立站 d1 留存基线 ~30%，d7 ~10%；本图色深 = 留存率高
                      </div>
                    </Card>
                  </Col>
                </Row>
              </>
            ),
          },

          // ============================================================
          // Tab 4：热区
          // ============================================================
          {
            key: 'heatmap',
            label: '④ 热区',
            children: (
              <>
                {filterBar}

                <Card size="small" style={{ marginBottom: 12 }}>
                  <Row gutter={12} align="middle">
                    <Col>
                      <span style={{ fontSize: 11, marginRight: 4 }}>页面:</span>
                      <Select
                        value={heatmapUrl ? undefined : selectedFlowPageId}
                        onChange={(v) => { flowPageTouchedRef.current = true; setSelectedFlowPageId(v); }}
                        style={{ width: 240 }}
                        size="small"
                        showSearch
                        optionFilterProp="children"
                        disabled={!!heatmapUrl}
                        placeholder={heatmapUrl ? 'URL 直达模式' : undefined}
                      >
                        {pageOptions.map(p => (
                          <Select.Option key={p.id} value={p.id}>{p.title}</Select.Option>
                        ))}
                      </Select>
                    </Col>
                    <Col>
                      <span style={{ fontSize: 11, marginRight: 4 }}>或 URL 直达:</span>
                      <Input.Search
                        size="small"
                        style={{ width: 320 }}
                        placeholder="粘贴 storefront 页面完整 URL 回车"
                        allowClear
                        onSearch={v => setHeatmapUrl((v || '').trim())}
                      />
                    </Col>
                    <Col>
                      <span style={{ fontSize: 11, marginRight: 4 }}>设备:</span>
                      <Select value={heatmapDevice} onChange={setHeatmapDevice} style={{ width: 100 }} size="small">
                        <Select.Option value="desktop">🖥 desktop</Select.Option>
                        <Select.Option value="mobile">📱 mobile</Select.Option>
                      </Select>
                    </Col>
                    {usingApi && heatmapUrl && !(apiState.heatmap?.clicks || []).length ? (
                      <Col><span style={{ fontSize: 11, color: '#fa8c16' }}>该页近 7 天无点击数据</span></Col>
                    ) : null}
                  </Row>
                </Card>

                <Row gutter={12} style={{ marginBottom: 12 }}>
                  <Col span={24}>
                    <Card size="small" title={<span><DesktopOutlined /> 页面热区（截图叠加）</span>} extra={
                      <span style={{ fontSize: 11, color: '#8c8c8c' }}>{apiState.heatmap?.pageUrl || ''}</span>
                    }>
                      {usingApi && (viewportHeatmap.desktop || []).some(point => (point as HeatmapOverlayPoint).selector) ? (
                        !shotFailed && apiState.heatmap?.pageId ? (
                          <ScreenshotHeatmap
                            webId={IDP_WEB_ID[apiState.heatmap.webKey] || 3}
                            pageId={apiState.heatmap.pageId}
                            device={heatmapDevice}
                            points={viewportHeatmap.desktop as HeatmapOverlayPoint[]}
                            scrollBuckets={apiState.heatmap.scrollBuckets}
                            onUnavailable={() => setShotFailed(true)}
                          />
                        ) : (
                          <>
                            <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8 }}>
                              该页截图尚未生成（SG 管线每日抓取近 7 天有点击的页面，新页最多等一天）——先看元素排行
                            </div>
                            <ElementClickMap
                              pageUrl={apiState.heatmap?.pageUrl}
                              points={viewportHeatmap.desktop as HeatmapOverlayPoint[]}
                            />
                          </>
                        )
                      ) : (
                        <Empty description="该页暂无点击数据" style={{ padding: 32 }} />
                      )}
                    </Card>
                  </Col>
                </Row>

                {usingApi && !shotFailed && (viewportHeatmap.desktop || []).some(point => (point as HeatmapOverlayPoint).selector) ? (
                  <Card size="small" title="元素点击排行（辅助视图）" style={{ marginBottom: 12 }} extra={
                    <span style={{ fontSize: 11, color: '#8c8c8c' }}>hover 看 selector / 点击数 / 独立用户</span>
                  }>
                    <ElementClickMap
                      pageUrl={apiState.heatmap?.pageUrl}
                      points={viewportHeatmap.desktop as HeatmapOverlayPoint[]}
                    />
                  </Card>
                ) : null}

                <Card size="small" title="Scroll 完读率（每页用户读到 100% 的比例）">
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8 }}>
                    业务含义：完读率低 = 用户上来就跑 / 中段流失；A/B 改完率走势可验证"短描述 vs 长描述"哪种被看完
                  </div>
                  <Table
                    size="small"
                    pagination={{ pageSize: 10 }}
                    rowKey="page_id"
                    dataSource={scrollReadRate}
                    columns={[
                      { title: '页面', dataIndex: 'title', ellipsis: true },
                      {
                        title: '完读率（读到 100%）', dataIndex: 'reached_100_pct', width: 260,
                        render: v => (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                            <Progress percent={v * 100} showInfo={false} size="small" style={{ width: 140 }} strokeColor={v > 0.5 ? '#52c41a' : v > 0.3 ? '#fa8c16' : '#cf1322'} />
                            <strong>{(v * 100).toFixed(1)}%</strong>
                          </div>
                        ),
                      },
                      {
                        title: '顶部弃读', dataIndex: 'topBounce_pct', width: 120,
                        render: v => <Tag color={v > 0.4 ? 'red' : v > 0.2 ? 'orange' : 'green'}>{(v * 100).toFixed(1)}%</Tag>,
                      },
                    ]}
                  />
                </Card>
              </>
            ),
          },

          // ============================================================
          // Tab 5：聚合统计（destFunction 维度）
          // ============================================================
          {
            key: 'aggregate',
            label: '⑤ 聚合统计',
            children: (
              <>
                {filterBar}

                <Card size="small" title="元素点击聚合" extra={
                  <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                    {aggregateGroups.length} 个元素 · 共 {aggregateGroups.reduce((s, g) => s + g.pv, 0)} 次点击 · 日期区间与筛选栏联动
                  </span>
                }>
                  <Row gutter={8} align="middle" style={{ marginBottom: 12 }}>
                    <Col><span style={{ fontSize: 11 }}>模块:</span></Col>
                    <Col>
                      <Select
                        size="small"
                        style={{ width: 200 }}
                        value={selectedModule}
                        onChange={setSelectedModule}
                        allowClear
                        showSearch
                        placeholder="全部模块"
                        optionFilterProp="children"
                      >
                        {moduleOptions.map(name => (
                          <Select.Option key={name} value={name}>{name}</Select.Option>
                        ))}
                      </Select>
                    </Col>
                    <Col><span style={{ fontSize: 11 }}>destFunction:</span></Col>
                    <Col>
                      <Select
                        size="small"
                        style={{ width: 300 }}
                        value={selectedDestFunction}
                        onChange={setSelectedDestFunction}
                        allowClear
                        showSearch
                        placeholder="全部"
                        optionFilterProp="children"
                      >
                        {(apiState.destFunctions || []).map(name => (
                          <Select.Option key={name} value={name}>{name}</Select.Option>
                        ))}
                      </Select>
                    </Col>
                  </Row>
                  <Table
                    size="small"
                    rowKey="label"
                    pagination={{ pageSize: 20, showSizeChanger: false }}
                    dataSource={aggregateGroups}
                    expandable={{
                      rowExpandable: (g: any) => (g.rows || []).length > 0,
                      expandedRowRender: (g: any) => (
                        <Table
                          size="small"
                          rowKey={(row: IdpAggregateRow) => `${row.elementId || row.selector || ''}_${row.eventType || ''}`}
                          pagination={false}
                          dataSource={g.rows}
                          columns={[
                            {
                              title: 'selector',
                              dataIndex: 'selector',
                              render: (s: string) => <code style={{ fontSize: 10 }}>{s}</code>,
                            },
                            {
                              title: '语义来源',
                              dataIndex: 'semanticRole',
                              width: 150,
                              render: (r: string) => (r ? <Tag style={{ fontSize: 10 }}>{r}</Tag> : '-'),
                            },
                            {
                              title: '事件类型',
                              dataIndex: 'eventType',
                              width: 120,
                              render: (t: string) => (t ? <Tag color={EVENT_TYPE_COLORS[t as EventType]} style={{ fontSize: 10 }}>{t}</Tag> : '-'),
                            },
                            { title: '点击', dataIndex: 'pv', width: 90 },
                            { title: '独立用户', dataIndex: 'uv', width: 90 },
                          ]}
                        />
                      ),
                    }}
                    columns={[
                      {
                        title: 'destFunction / 元素',
                        dataIndex: 'label',
                        ellipsis: true,
                      },
                      {
                        title: '模块',
                        dataIndex: 'module',
                        width: 180,
                        render: (m: string) => <Tag style={{ fontSize: 10 }}>{m}</Tag>,
                      },
                      {
                        title: '点击',
                        dataIndex: 'pv',
                        width: 110,
                        sorter: (a: any, b: any) => (a.pv || 0) - (b.pv || 0),
                        defaultSortOrder: 'descend' as const,
                        render: (v: number) => <strong>{v || 0}</strong>,
                      },
                      {
                        title: '独立用户',
                        dataIndex: 'uv',
                        width: 110,
                        sorter: (a: any, b: any) => (a.uv || 0) - (b.uv || 0),
                        render: (v: number, g: any) => (
                          (g.rows || []).length > 1
                            ? <Tooltip title="该元素多种事件/selector 变体，独立用户跨行不可去重相加，此处取最大变体值（下界）"><span>≥{v || 0}</span></Tooltip>
                            : (v || 0)
                        ),
                      },
                      { title: '明细', dataIndex: 'rows', width: 80, render: (rows: any[]) => `${(rows || []).length} 行` },
                    ]}
                    summary={() => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>{null}</Table.Summary.Cell>
                        <Table.Summary.Cell index={1}><strong>总计</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>{null}</Table.Summary.Cell>
                        <Table.Summary.Cell index={3}><strong>{aggregateGroups.reduce((s, g) => s + g.pv, 0)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          <Tooltip title="独立用户跨元素不可去重相加，总 UV 看总览 tab"><span>—</span></Tooltip>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>{null}</Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                    locale={{ emptyText: usingApi ? '该日期区间无聚合数据' : 'API 不可用（mock 模式无聚合统计）' }}
                  />
                </Card>
              </>
            ),
          },
        ]}
      />
    </div>
  );
};

export default TrafficAnalysis;
